import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter, Subject, takeUntil } from 'rxjs';
import { PaymentPlanDialogComponent } from 'src/app/dialogs/payment-plan-dialog/payment-plan-dialog.component';
import {
    EMAIL_VALIDATOR_REG_EXP,
    NUMBERS_ONLY,
    StatusLabelType,
} from 'src/app/store/constants/common.constants';
import { IStatusLabel } from 'src/app/store/interfaces';
import { ApiService } from 'src/app/store/services';
import { SnackbarService } from 'src/app/store/services/snackbar.service';
import { skin } from 'src/white-labels';

@Component({
    selector: 'app-quick-activation',
    templateUrl: './quick-activation.component.html',
    styleUrls: ['./quick-activation.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuickActivationComponent implements OnDestroy {
    quickActivationForm: UntypedFormGroup = this.formBuilder.group({
        email: ['', [Validators.required, Validators.pattern(EMAIL_VALIDATOR_REG_EXP)]],
        trackerID: [null, [Validators.required, Validators.minLength(7), Validators.maxLength(11)]],
        IMEI: [
            null,
            [
                Validators.required,
                Validators.minLength(4),
                Validators.maxLength(4),
                Validators.pattern(NUMBERS_ONLY),
            ],
        ],
    });

    skin = skin;
    isShowDeviceInfo = false;
    isShowPlansInfo = false;
    isShowCongratulations = false;

    dt: number;
    slectedPlan: any;
    planDTOList: any[];

    private destroyed$ = new Subject<void>();

    constructor(
        private formBuilder: UntypedFormBuilder,
        public dialog: MatDialog,
        private router: Router,
        private route: ActivatedRoute,
        private apiService: ApiService,
        private cdRef: ChangeDetectorRef,
        private translate: TranslateService,
        private snackBar: SnackbarService,
    ) {
        if (skin.whiteLabel !== 'MYLOC8' && skin.whiteLabel !== 'PETLOC8') {
            this.router.navigate(['/']);
        }

        this.route.queryParams.subscribe((params) => {
            this.dt = params.dt ?? 1;
        });
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    onSubmit(isValid: boolean) {
        this.quickActivationForm.markAllAsTouched();

        if (isValid) {
            this.apiService
                .getPlanListForQuickActivation({
                    email: this.quickActivationForm.value.email,
                    device_id: this.quickActivationForm.value.trackerID,
                    imei: this.quickActivationForm.value.IMEI,
                    dt: this.dt,
                })
                .pipe(takeUntil(this.destroyed$))
                .subscribe(
                    (res) => {
                        this.planDTOList = res.planDTOList;
                        this.slectedPlan =
                            res.planDTOList.find((plan) => plan.clubPromotion) || res.planDTOList[0];
                        this.isShowPlansInfo = true;
                        this.cdRef.markForCheck();
                    },
                    ({ error }) => this.snackBar.warning(this.translate.instant(error.message)),
                );
        }
    }

    selectPlan(plan): void {
        this.slectedPlan = plan;
    }

    onSubscribe(): void {
        const dialog = this.dialog.open(PaymentPlanDialogComponent, {
            maxWidth: '530px',
            minWidth: '460px',
            backdropClass: 'dialogBackground',
            data: {
                ...this.slectedPlan,
                deviceID: this.quickActivationForm.value.trackerID,
                email: this.quickActivationForm.value.email,
            },
        });

        dialog
            .afterClosed()
            .pipe(filter((response) => Boolean(response)))
            .subscribe(() => {
                this.apiService
                    .activateDevice({
                        email: this.quickActivationForm.value.email,
                        device_id: this.quickActivationForm.value.trackerID,
                        plan_id: this.slectedPlan.id,
                        dt: this.dt,
                    })
                    .pipe(takeUntil(this.destroyed$))
                    .subscribe((res) => {
                        this.isShowCongratulations = true;
                        this.cdRef.markForCheck();
                    });
            });
    }

    toggleInfo(): void {
        this.isShowDeviceInfo = !this.isShowDeviceInfo;
    }

    goToLogin(): void {
        this.router.navigate(['/']);
    }
}
